import { format } from "date-fns";
import { Calendar, Command, Download, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { MobileNavTrigger } from "./MobileNav";
import { NotificationsBell } from "./NotificationsBell";

interface AppHeaderProps {
  subtitle?: string;
  ctaLabel?: string;
  onCta?: () => void;
  onExport?: () => void;
  extraActions?: React.ReactNode;
}

function greet() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function AppHeader({ subtitle, ctaLabel, onCta, onExport, extraActions }: AppHeaderProps) {
  const { profile } = useAuth();
  const first = (profile?.full_name || profile?.email || "there").split(" ")[0];
  const today = new Date();
  const sub = subtitle ?? "Welcome back to your workspace";
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <div className="flex flex-col gap-3 border-b border-border px-4 py-[18px] sm:px-[22px] md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-2">
        <MobileNavTrigger />
        <div>
          <h1 className="text-[18px] font-bold leading-tight">
            {greet()}, {first} 👋
          </h1>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {format(today, "EEE, dd MMM")} · {sub}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          aria-label="Open command palette"
          onClick={() => {
            window.dispatchEvent(
              new KeyboardEvent("keydown", {
                key: "k",
                metaKey: isMac,
                ctrlKey: !isMac,
              }),
            );
          }}
          className="hidden h-9 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-muted-foreground hover:bg-muted sm:flex"
        >
          <Command className="h-3.5 w-3.5" />
          <span>{isMac ? "⌘" : "Ctrl"} K</span>
        </button>
        <button className="hidden h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-[11px] font-medium text-foreground hover:bg-muted sm:flex">
          <Calendar className="h-3.5 w-3.5" />
          {format(today, "MMM yyyy")}
        </button>
        <div
          className="hidden h-9 items-center rounded-md px-3 text-[12px] font-semibold text-white shadow-sm sm:flex"
          style={{ background: "var(--gradient-primary)" }}
          aria-label="Workspace Health"
        >
          Workspace Health 87%
        </div>
        <NotificationsBell />
        {extraActions}
        {onExport && (
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-[11px]"
            onClick={onExport}
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        )}
        {ctaLabel && (
          <Button size="sm" className="h-9 text-[11px]" onClick={onCta}>
            <Plus className="h-3.5 w-3.5" />
            {ctaLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
